import React, { useState, useEffect } from 'react';
import { DatePickerComponent, ChangedEventArgs, MaskedDateTime, Inject } from '@syncfusion/ej2-react-calendars';

interface IInputDatePickerProps {
  id: string;
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  value?: any;
  min?: Date;
  max?: Date;
  showClearButton?: boolean;
  cssClass?: string;
  onChange?: (value: string | null) => void;
  format?: string;
  errorMessage?: string;
  minErrorMessage?: string;
  maxErrorMessage?: string;
}

const MIN_DATE = new Date(1900, 0, 1);
const MAX_DATE = new Date(2099, 11, 31);
const MASK_PLACEHOLDER = { day: 'dd', month: 'mm', year: 'aaaa' };

const toDate = (value: any): Date | undefined => {
  if (!value)
    return undefined;

  if (value instanceof Date)
    return isNaN(value.getTime()) ? undefined : value;

  if (typeof value === 'string') {
    // Handle DD/MM/YYYY format
    const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const [, dd, mm, yyyy] = ddmmyyyy;
      const date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      return isNaN(date.getTime()) ? undefined : date;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
};

const InputDatePicker: React.FC<IInputDatePickerProps> = ({
  id,
  name,
  label,
  placeholder = 'dd/mm/aaaa',
  disabled = false,
  required = false,
  value,
  min = MIN_DATE,
  max = MAX_DATE,
  showClearButton = true,
  cssClass = '',
  onChange,
  format = 'dd/MM/yyyy',
  errorMessage,
  minErrorMessage,
  maxErrorMessage
}) => {
  const [internalError, setInternalError] = useState<string>('');

  const stripTime = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const validateDate = (date: Date | null) => {
    if (!date) {
      setInternalError('');
      return;
    }
    const dateOnly = stripTime(date);
    if (min && min !== MIN_DATE && dateOnly < stripTime(min)) {
      setInternalError(minErrorMessage || 'La fecha no puede ser anterior a la fecha mínima permitida');
      return;
    }
    if (max && max !== MAX_DATE && dateOnly > stripTime(max)) {
      setInternalError(maxErrorMessage || 'La fecha no puede ser posterior a la fecha máxima permitida');
      return;
    }
    setInternalError('');
  };

  useEffect(() => {
    const parsed = toDate(value);
    if (parsed) {
      validateDate(parsed);
    } else {
      setInternalError('');
    }
  }, [value, min, max]);

  const handleChange = (args: ChangedEventArgs) => {
    if (args.value) {
      validateDate(args.value);
    } else {
      setInternalError('');
    }
    if (onChange) {
      if (args.value) {
        const d = args.value;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        onChange(`${dd}/${mm}/${yyyy}`);
      } else {
        onChange(null);
      }
    }
  };

  const displayError = errorMessage || internalError;

  return (
    <div className={`input-date-picker${displayError ? ' error' : ''}`}>
      {label &&
        <label htmlFor={id} className="input-block__label" > {label} </label>
      }
      <DatePickerComponent
        id={id}
        name={name}
        placeholder={placeholder}
        format={format}
        locale="es"
        firstDayOfWeek={1}
        enableMask={true}
        maskPlaceholder={MASK_PLACEHOLDER}
        strictMode={true}
        min={min}
        max={max}
        showClearButton={showClearButton}
        enabled={!disabled}
        value={toDate(value)}
        change={handleChange}
        cssClass={cssClass || (displayError ? 'error-validate' : '')}
        htmlAttributes={required ? { required: 'true' } as any : undefined}
      >
        <Inject services={[MaskedDateTime]} />
      </DatePickerComponent>
      {displayError &&
        <p className="error m-0" > {displayError} </p>
      }
    </div>
  );
};

export default InputDatePicker;